import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { EmptyState } from '@/components/ui/EmptyState';
import { ICONS } from '@/constants/icons';

/**
 * Empty state shown when a settings tab is brand-scoped but the viewer (agency staff)
 * has not entered a brand yet, so there is no team context to load.
 */
export const NoBrandSelected = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  return (
    <EmptyState
      icon={ICONS.TEAM}
      title={t('settings_no_brand_title')}
      description={t('settings_no_brand_description')}
      action={
        <button
          type="button"
          onClick={() => navigate('/agency/portfolio')}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          {t('settings_no_brand_cta')}
        </button>
      }
    />
  );
};

/** Renders children only when a brand (team) context exists; otherwise the friendly empty state. */
export const BrandScopedTab = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  if (user && !user.currentTeamId && !user.teamId) return <NoBrandSelected />;
  return <>{children}</>;
};
